import React from 'react';
import styled from 'styled-components';
import { Grid, Container } from 'semantic-ui-react';
import { useGoogleAds } from 'hooks/useGoogleAds';
import { ContentSideFilterContainer } from 'containers/ContentSideFilterContainer';
import { AggregationEventsContainer } from 'containers/AggregationEventsContainer';
import { CombinedFiltersPaginationViewContainer } from 'containers/CombinedFiltersPaginationViewContainer';
import { NoResults } from 'components/molecules/NoResults/NoResults';
import { Footer } from 'components/molecules/Footer';
import { SocialSharing } from 'components/molecules/SocialSharing/SocialSharing';
import { OnlyMobile, PageHelmet } from 'components/atoms';
import { ExternalAdBanner, ExternalAdBannerType } from 'components/organisms/ExternalAdBanner/ExternalAdBanner';
import { HeaderPageWrapper } from 'components/templates/HeaderPageWrapper';
import { PaginationInt, sortByOptions } from 'constants/index';
import { Contentful } from 'mxp-schemas';
import { PromotionalBanner } from 'components/molecules/PromotionalBanner/PromotionalBanner';

export interface Props {
  isAuth: boolean | null;
  sortBy: string;
  page: number;
  perPage: number;
  filtersStr: string; // subscription required to fire fetchSearchContent if changed
  pagination: PaginationInt;
  resultsLoading: boolean;
  resultsFetched: boolean;
  results: State.FeedCardContentItem[];
  productType: Contentful.ProductTypes.ProductType;
  fetchCalendarSearchContent: () => void;
  resetSearchModule: () => void;
  toggleLoginReload(shouldReload: boolean): void;
  resetSkipSearchFrontloadRequest: () => void;
  skipSearchFrontloadRequest: boolean;
  promotionalBannerProduct: State.ContentCardItem | null;
  pageTitle: string;
  productAggPageContent: State.ProductAggsPageContent;
}

export const PageProductCalendar: React.FC<Props> = ({
  sortBy,
  page,
  perPage,
  pagination,
  resultsLoading,
  resultsFetched,
  results,
  productType,
  resetSearchModule,
  promotionalBannerProduct,
  pageTitle,
  productAggPageContent,
}) => {
  useGoogleAds(productAggPageContent?.externalAdsConfig);
  const isNoResults: boolean = Boolean(resultsFetched && !resultsLoading && results.length === 0);
  React.useEffect(() => resetSearchModule, [resetSearchModule]);
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const toggleModalOpen = React.useCallback(() => setModalOpen(!modalOpen), [modalOpen, setModalOpen]);

  const isWebcastPassPublished = Boolean(promotionalBannerProduct?.published);

  const metaTitle = productType?.metaTitle;
  const metaDescription = productType?.metaDescription;

  const renderWebcastPassBanner = (): React.ReactNode => {
    if (!promotionalBannerProduct || !isWebcastPassPublished) return null;
    return (
      <PromotionalBanner
        title="You can take CPE in stride"
        description="If you're wondering how to carve out time for professional development, consider achieving CPE credits over the course of a year with the Annual Webcast Pass."
        testId="webcast-pass-banner"
        titleTestId="webcast-pass-banner-title"
        descriptionTestId="webcast-pass-banner-desc"
        showBackground
        isLeftAligned
        product={promotionalBannerProduct}
      />
    );
  };

  return (
    <>
      <PageHelmet
        title={`AICPA & CIMA Learning Products | ${pageTitle || productType?.title}`}
        meta={{
          title: metaTitle,
          description: metaDescription,
        }}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
        }}
        usePagination
      />
      <OnlyMobile>
        <ContentSideFilterContainer
          isModalOpen={modalOpen}
          closeModal={toggleModalOpen}
          resultsNumber={pagination.total}
          resultsFetched={resultsFetched}
          hideCategoryFilter
          hideDatesFilter
        />
      </OnlyMobile>
      <HeaderPageWrapper>
        {productAggPageContent?.externalAdsConfig?.topAdBanner && <ExternalAdBanner type={ExternalAdBannerType.TOP} />}
        <AggregationEventsContainer />
        {isNoResults ? (
          <StyledContainer>
            <Grid>
              <Grid.Column computer={4} tablet={16} />
              <Grid.Column computer={12} tablet={16}>
                <NoResults />
              </Grid.Column>
            </Grid>
          </StyledContainer>
        ) : (
          <StyledWrapper>
            <CombinedFiltersPaginationViewContainer
              sortByOptionItems={Object.values(sortByOptions).filter(item => item.value !== 'relevancy')}
              sortBy={sortBy}
              perPage={perPage}
              pagination={pagination}
              page={page}
              resultsFetched={resultsFetched}
              resultsLoading={resultsLoading}
              results={results}
              toggleModalOpen={toggleModalOpen}
              renderPromotionalBanner={renderWebcastPassBanner}
              isCPELearningPage
              isGridView
              hideCategoryFilter
              hideDatesFilter
            />
          </StyledWrapper>
        )}
        {productAggPageContent?.externalAdsConfig?.bottomAdBanner && (
          <ExternalAdBanner type={ExternalAdBannerType.BOTTOM} />
        )}
        <Footer />
        <SocialSharing title={metaTitle} isSticky />
      </HeaderPageWrapper>
    </>
  );
};

const StyledContainer = styled(Container)`
  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    min-height: calc(100vh - ${props => props.theme.pxToRem(170)});
  }
`;

const StyledWrapper = styled.div`
  padding-top: ${props => props.theme.pxToRem(40)};
`;
